import React from 'react';
import { ArrowLeft, Play, Star, Smartphone, Monitor, CreditCard, Users, Heart, MessageSquare } from 'lucide-react';

const LearnMore = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <a href="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </a>

        <h1 className="text-4xl font-bold mb-4">About Pulse Media</h1>
        <p className="text-gray-400 mb-12 text-lg">Your home for exclusive movies, TV shows, and music videos.</p>

        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">What is Pulse Media?</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
            Pulse Media is an exclusive streaming platform delivering content you won't find anywhere else. We spotlight independent creators and emerging talent across original series, films, and visual music content. Built for culture-forward audiences, we prioritize quality over quantity — every title in our catalog is curated with intention.
          </p>
          <p className="text-gray-300 leading-relaxed">
            We built Pulse Media because great entertainment shouldn't cost a fortune. For just $1.99 per month, you get unlimited access to our full library with no ads and no hidden fees. New content is added regularly, and subscribers get instant access to every new release.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-8">Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Play className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Unlimited Streaming</h3>
              <p className="text-gray-400 text-sm">Watch every movie, TV show, and music video in our library. No limits, no tiers, no content locked behind extra paywalls.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Star className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Exclusive Content</h3>
              <p className="text-gray-400 text-sm">Original series, independent films, and music videos from artists shaping culture. Content you won't find on any other platform.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Monitor className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Watch Anywhere</h3>
              <p className="text-gray-400 text-sm">Stream on the web at pulseportal.tv or on Android via the Pulse Media app. Your progress syncs across all your devices.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <CreditCard className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">$1.99/Month</h3>
              <p className="text-gray-400 text-sm">One price, everything included. No ads, no introductory tricks, no price hikes. Cancel anytime with no fees.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <Heart className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Favorites & Watch History</h3>
              <p className="text-gray-400 text-sm">Save content to your favorites and pick up exactly where you left off with continue watching. Never lose your place.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <MessageSquare className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Community</h3>
              <p className="text-gray-400 text-sm">Rate content, leave comments, and submit video reviews. Your voice matters — be part of the conversation.</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Our Content</h2>
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">Movies</h3>
              <p className="text-gray-400 text-sm">Independent films and exclusive titles spanning drama, action, comedy, and more. Every film in our catalog has been handpicked for our audience.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">TV Shows</h3>
              <p className="text-gray-400 text-sm">Original series with full seasons available to binge. New episodes and new shows added on a regular basis.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">Music Videos</h3>
              <p className="text-gray-400 text-sm">Visual music content from emerging and established artists. Watch the videos that are defining the sound of right now.</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Available Platforms</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Monitor className="h-10 w-10 text-cyan-400 mx-auto mb-3" />
              <h3 className="font-semibold mb-1">Web</h3>
              <p className="text-gray-400 text-sm">pulseportal.tv</p>
              <span className="inline-block mt-2 text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">Available Now</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Smartphone className="h-10 w-10 text-cyan-400 mx-auto mb-3" />
              <h3 className="font-semibold mb-1">Android</h3>
              <p className="text-gray-400 text-sm">Google Play Store</p>
              <span className="inline-block mt-2 text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">Available Now</span>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Smartphone className="h-10 w-10 text-gray-600 mx-auto mb-3" />
              <h3 className="font-semibold mb-1">iOS</h3>
              <p className="text-gray-400 text-sm">App Store</p>
              <span className="inline-block mt-2 text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded-full">Coming Soon</span>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Get Started</h2>
          <div className="bg-gradient-to-r from-cyan-400/10 to-cyan-400/5 border border-cyan-400/20 rounded-xl p-8 text-center">
            <p className="text-lg text-gray-300 mb-6">Join Pulse Media for $1.99/month and get instant access to our full library.</p>
            <div className="flex items-center justify-center space-x-4">
              <a href="/signup" className="px-8 py-3 bg-cyan-400 text-black rounded-lg font-semibold hover:bg-cyan-300 transition-colors">
                Create Account
              </a>
              <a href="/signin" className="px-8 py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Sign In
              </a>
            </div>
          </div>
        </section>

        <div className="text-center text-sm text-gray-500 space-x-4">
          <a href="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</a>
          <span>|</span>
          <a href="/terms" className="hover:text-gray-300 transition-colors">Terms of Use</a>
          <span>|</span>
          <a href="/support" className="hover:text-gray-300 transition-colors">Support</a>
          <span>|</span>
          <a href="/delete-account" className="hover:text-gray-300 transition-colors">Delete Account</a>
        </div>
      </div>
    </div>
  );
};

export default LearnMore;
